import {
  getPreferredEditor,
  getEditorForExtension,
  type EditorRegistration,
} from './editor-registry.js'
import { resolveAppFolderEditor } from './editors/miniapp-registration.js'
import { LARGE_CSV_ROUTING_ENABLED, isLargeCsv, isCsvOrTsvName } from '@papan/types'

// ─── Which editor opens a file, and how (#942) ──────────────────────────
//
// ONE rule for every surface that opens a file — the workspace tab, `/file/:id`
// and the share view. Each used to decide on its own and they disagreed (a 1.5 MB
// .html: "too large" in a tab, the full editor on /file/:id, source text in the
// share view). The rule:
//
//   a folder        → the app editor for an app folder, else nothing
//   else            → the registered editor for the extension (the user's
//                     preferred one where the surface has a preference)
//                     ?? (not binary ? the txt editor : nothing)
//   over the editor's `maxEditableSize` (the live editor's limit)
//                   → HTML: the SAME editor in preview-only (the last saved
//                     page, no live room — HtmlEditor `previewOnly`)
//                   → anything else: plain read-only text
//   a csv/tsv over LARGE_CSV_THRESHOLD_BYTES (#981, while the routing flag is
//   on)            → the windowed large-CSV grid — unless the file still has
//                     unsaved whole-file edits (the old editor until they are
//                     saved or discarded, spec §12)
//   over OVER_LIMIT_VIEW_MAX_SIZE
//                   → read-only text: nothing is loaded — a "too large to
//                     show" panel (the text view reads the whole file into
//                     one page)
//                   → HTML: still the preview, served by URL like any page;
//                     only its INLINE path (Single File Mode, or no preview
//                     session), which reads the page into the tab, is refused
//                     (`inlineTooLarge`)
//
// What a surface may DO with the choice (read-only, a share link's caps) is
// layered on top by the caller — this decides only WHICH editor and whether it
// is the full editor or its preview. Pure: the same inputs always answer the same.

/** What the caller learned about the bytes, when the choice needs it. */
export interface EditorProbe {
  /** A NUL byte in the first 512 bytes (the files API's `isBinary` rule). */
  isBinary: boolean
  /** Total size in bytes, when known. */
  size: number | null
}

export interface EditorChoiceInput {
  /** The file name the type is read from. For a file-target `.shared` row, the
   *  name of the file it points at (`report.html.shared` → `report.html`): it
   *  opens by the INNER type, by the same rule as any other file. */
  name: string
  isFolder?: boolean
  /** The file's real name, when `name` only carries its type (the drive passes `.<ext>`). The
   *  large-CSV rule is judged on it, as the server judges it (`isLargeCsv(file.name, …)`): a name
   *  like `.csv` alone is a dotfile, never a csv. Defaults to `name`. */
  fileName?: string
  /** The user's preferred editor id for this extension (drive surfaces only —
   *  a share visitor may be anonymous and has no settings). */
  preferredEditorId?: string | null
}

export type EditorChoice =
  /** The answer depends on the bytes (an unregistered type → the binary sniff,
   *  or an editor with a size limit). Probe and choose again. */
  | { kind: 'probe' }
  /** The registered editor. `previewOnly`: over its size limit — mount it in its
   *  preview-only mode (HtmlEditor). `inlineTooLarge`: preview-only past
   *  OVER_LIMIT_VIEW_MAX_SIZE — the page may be served, never read into the tab. */
  | { kind: 'editor'; registration: EditorRegistration; previewOnly: boolean; inlineTooLarge?: boolean }
  /** Plain read-only text: over the live editor's limit, for a type with no
   *  preview-only mode. */
  | { kind: 'text-viewer'; reason: 'too-large' }
  /** Too large even for read-only text (over OVER_LIMIT_VIEW_MAX_SIZE): show
   *  nothing of it (download instead). */
  | { kind: 'too-large' }
  /** Nothing can open it: a plain folder, or an unregistered binary file. */
  | { kind: 'unsupported'; reason: 'folder' | 'binary' }
  /** A csv/tsv over LARGE_CSV_THRESHOLD_BYTES: the windowed large-CSV grid (#981). */
  | { kind: 'large-csv' }

/** What the caller knows beyond the name and the bytes. */
export interface EditorChoiceOptions {
  /** The file has a whole-file collab room with unsaved edits (`GET /api/csv/:id/legacy-edits`):
   *  a large csv keeps the old editor until they are saved or discarded (#981 spec §12). */
  hasUnsavedWholeFileEdits?: boolean
  /** The file has unsaved LARGE-GRID edits (fragment rows on any base, same answer): a csv/tsv keeps the large
   *  grid whatever its size, until they are saved or discarded (#981 QA 14). Whole-file edits win over these. */
  hasFragmentEdits?: boolean
  /** Overrides LARGE_CSV_ROUTING_ENABLED (tests). */
  largeCsvRouting?: boolean
}

/** The lower-cased extension of a file name ('' for none). */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

/** The largest over-limit file read INTO the tab: read-only text (the whole file
 *  into one page, again on every update), or an HTML preview's inline page (Single
 *  File Mode). A 600 MB log would freeze the tab; past this a text file is offered
 *  for download instead, and an HTML page is only served (by URL, like any page). */
export const OVER_LIMIT_VIEW_MAX_SIZE = 5 * 1024 * 1024

/** The editor families that can show an over-limit file as a preview. */
const PREVIEW_ONLY_FAMILIES = new Set<EditorRegistration['family']>(['html-page'])

export function chooseEditor(input: EditorChoiceInput, probe?: EditorProbe | null, opts?: EditorChoiceOptions): EditorChoice {
  const ext = extensionOf(input.name)
  if (input.isFolder) {
    const app = resolveAppFolderEditor(ext, true)
    return app ? { kind: 'editor', registration: app, previewOnly: false } : { kind: 'unsupported', reason: 'folder' }
  }
  const registered = getPreferredEditor(ext, input.preferredEditorId ?? undefined)
  if (!registered) {
    if (!probe) return { kind: 'probe' }
    if (probe.isBinary) return { kind: 'unsupported', reason: 'binary' }
  }
  const registration = registered ?? getEditorForExtension('txt')
  if (!registration) return { kind: 'unsupported', reason: 'binary' }
  if (registration.family === 'spreadsheet' && (opts?.largeCsvRouting ?? LARGE_CSV_ROUTING_ENABLED)) {
    // The size decides between the whole-file editor and the windowed grid.
    if (!probe) return { kind: 'probe' }
    const csvName = input.fileName ?? input.name
    const large = isLargeCsv(csvName, probe.size) || (opts?.hasFragmentEdits === true && isCsvOrTsvName(csvName))
    if (large && !opts?.hasUnsavedWholeFileEdits) return { kind: 'large-csv' }
  }
  if (registration.maxEditableSize) {
    if (!probe) return { kind: 'probe' }
    if (probe.size != null && probe.size > registration.maxEditableSize) {
      const pastView = probe.size > OVER_LIMIT_VIEW_MAX_SIZE
      if (PREVIEW_ONLY_FAMILIES.has(registration.family)) {
        return pastView
          ? { kind: 'editor', registration, previewOnly: true, inlineTooLarge: true }
          : { kind: 'editor', registration, previewOnly: true }
      }
      return pastView ? { kind: 'too-large' } : { kind: 'text-viewer', reason: 'too-large' }
    }
  }
  return { kind: 'editor', registration, previewOnly: false }
}
